/**
 * Single source of truth for integrator **hub modules** (app switcher entries, RBAC keys, deep-link roots).
 *
 * When you add deep linking under a new top-level path:
 * 1. Append a row here (`key` must match `public.integrator_role_subapp.subapp_key` when using the matrix).
 * 2. Register routes in `ui/src/App.tsx` and wrap the layout with `SubappAccessGuard` using the same `key`.
 * 3. Add `NAV_BY_APP` entries in `ui/src/lib/appNavigation.ts` (module nav + module home cards). Co-home a page on every module that can open it. Go to… is derived from this list; `npm run quick-switcher:coverage` is a required `dev:gate` step.
 * 4. Extend `ui/src/components/Layout.tsx` `app` prop union when the module has a layout shell.
 * 5. Add a migration updating `integrator_role_subapp_subapp_check` and seeding default role grants.
 * 6. Deploy — the app switcher, module access matrix, access-request emails, and AccessNeededPage “hub link map”
 *    all pick this list up automatically.
 *
 * @see `GET /api/integrator-modules` — same data for API clients.
 */

export const INTEGRATOR_MODULE_DEFINITIONS = [
  {
    key: "erp",
    label: "Data connectors",
    path: "/erp",
    description:
      "Ingest controls, run history, findings, integrator settings, and ERPNext sync. Paths under this root are deep-linkable; access follows the module access matrix (integrator administrators always have this module).",
  },
  {
    key: "mrp",
    label: "MRP",
    path: "/mrp",
    description:
      "Forecast, planning summaries, build plan, assemblies, SKUs, AML, purchase orders, incoming parts, and nested routes (for example SKU detail, purchase order detail).",
  },
  {
    key: "clampqc",
    label: "Clamp quality control",
    path: "/clamp-qc",
    description:
      "Pick a clamp build, record line inspections on the job workspace, and open reporting when you need roll-ups or every inspection line.",
  },
  {
    key: "manufacturing",
    label: "Manufacturing",
    path: "/manufacturing",
    description:
      "Shop-floor kitting, work orders, encapsulation and final quality control flows, quality incidents, reporting, and manufacturing settings.",
  },
  {
    key: "manufacturing_management",
    label: "Manufacturing management",
    path: "/manufacturing-management",
    description: "Incoming orders, CM purchase orders, alternates board, production calendar, and production capacity planner. Paths under this root share the same access gate.",
  },
  {
    key: "documents",
    label: "Document Control",
    path: "/documents",
    description:
      "Controlled document revisions, ECOs, Product Collections, transition notices, and document-control history. Off: the module is hidden; staff can still see titles on a SKU page and request access. Read only: lists in Document Control, no preview sidebar and no download unless a file is shared with you. Read + write: lists, preview sidebar, and upload or edit; no download unless a file is shared with you. Module administrator: preview, download, share, and approve access requests. Integrator administrator counts as administrator in every module, including this one. A share does not open the Document Control module; people without the module still see shared files on the SKU page, including preview and download. Copying a controlled file onto an RFQ is a download, so it needs administrator access or a share.",
  },
  {
    key: "wiki",
    label: "Wiki",
    path: "/wiki",
    description: "Internal Smart Grid Solutions wiki and team knowledge base.",
  },
  {
    key: "bom",
    label: "BoM management",
    path: "/bom",
    description: "Bill of materials browser, rule workbench, approval, and test-revision routes.",
  },
  {
    key: "admin",
    label: "Admin",
    path: "/admin",
    description: "Hub accounts, module access, locations, payment labels, distributor APIs, and other admin tools.",
  },
  {
    key: "shipping",
    label: "Shipping",
    path: "/shipping",
    description:
      "Outbound shipments and inbound receiving at Smart Grid Solutions. Mark shipments ready, record carrier and tracking, receive POs and transfers, upload packing slips, and run Arrivals, Receive, Holding, and To stock stations.",
  },
  {
    key: "warehouse",
    label: "Warehouse",
    path: "/warehouse",
    description:
      "Warehouse floor tools: recount SKU on-hand, adjust scannable cartons, and carve new carton labels from unclaimed bulk quantity at a location.",
  },
  {
    key: "accounting",
    label: "Accounting",
    path: "/accounting",
    description:
      "Vendor invoice intake and QuickBooks match confirmation, plus read-only cash flow forecast from purchase orders, sales orders, and posted invoices.",
  },
  {
    key: "quality",
    label: "Quality",
    path: "/quality",
    description:
      "ISO 9001 quality cases and investigations, RMAs (return authorizations, intake, UPS return labels), corrective action reports, and device lookup.",
  },
  {
    key: "marketing",
    label: "Marketing",
    path: "/marketing",
    description:
      "Label artwork and ZPL template management: preview, version, activate, and test-print hub-owned label families.",
  },
  {
    key: "art_and_labels",
    label: "Art and Labels",
    path: "/art-and-labels",
    description:
      "Vendor label artwork, SKU-to-art mappings, and purchase-order CSVs for the print house. Separate from Personal office printing and Marketing ZPL templates.",
  },
  {
    key: "printing",
    label: "Printing",
    path: "/printing",
    description:
      "Label purchase order CSV generation, label SKU to art mappings, and art file rules formerly in Art and Labels.",  },
  {
    key: "wireless",
    label: "Wireless",
    path: "/wireless",
    description:
      "Wireless portal users, device registration, telemetry mirrors, and issue workspaces. Read tools are on hub MCP (wireless:read).",
  },
  {
    key: "inside_sales",
    label: "Inside Sales",
    path: "/inside-sales",
    description:
      "Create and approve sales orders, manage approval tickets, customers, new product SKUs, and rep agency commission rates.",
  },
  {
    key: "commissions",
    label: "Commissions",
    path: "/commissions",
    description:
      "Monthly sales-rep commission runs, bonuses, regional stocking, statements, and reconciliation.",
  },
  {
    key: "engineering",
    label: "Engineering",
    path: "/engineering",
    description:
      "Firmware releases, approval workflow, and downloadable build artifacts formerly tracked in ERPNext Firmware.",
  },
  {
    key: "people",
    label: "People",
    path: "/people",
    description:
      "Staff directory, job and lifespan dates, and the apps each person was given so offboarding has a checklist. Hub roles stay in Admin.",
  },
  {
    key: "personal",
    label: "Personal",
    path: "/personal",
    description:
      "Your notifications, time clock, office printing, account settings, reimbursements, purchase requests, support tickets, and ChatGPT MCP connector authorizations.",
  },
] as const;

/** Open via avatar menu and deep links; not listed in the app switcher. */
export const INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER: ReadonlySet<string> = new Set(["personal"]);

export type IntegratorModuleDefinition = (typeof INTEGRATOR_MODULE_DEFINITIONS)[number];

export type IntegratorSubappKey = IntegratorModuleDefinition["key"];

export const INTEGRATOR_SUBAPP_KEYS: readonly IntegratorSubappKey[] = INTEGRATOR_MODULE_DEFINITIONS.map(
  (row) => row.key,
);

export const INTEGRATOR_SUBAPP_KEYS_SET = new Set<string>(INTEGRATOR_SUBAPP_KEYS);

export function integratorModuleByKey(key: string): IntegratorModuleDefinition | undefined {
  return INTEGRATOR_MODULE_DEFINITIONS.find((row) => row.key === key);
}

export function integratorModuleEntryPath(key: string): string | undefined {
  return integratorModuleByKey(key)?.path;
}

export function integratorModuleLabel(key: string): string {
  return integratorModuleByKey(key)?.label ?? key;
}
